import {
    MakeLogicType,
    actions,
    connect,
    kea,
    key,
    listeners,
    path,
    props,
    propsChanged,
    reducers,
    selectors,
} from 'kea'

import { TaxonomicFilterGroupType } from 'lib/components/TaxonomicFilter/types'
import { objectsEqual } from 'lib/utils/objects'
import { FIELD_VALUES, SCALE_FIELD_VALUES } from 'scenes/cohorts/CohortFilters/constants'
import { BehavioralFilterKey, FieldOptionsType, FieldValues } from 'scenes/cohorts/CohortFilters/types'
import { cleanBehavioralTypeCriteria, resolveCohortFieldValue } from 'scenes/cohorts/cohortUtils'
import { userLogic } from 'scenes/userLogic'

import { actionsModel } from '~/models/actionsModel'
import { cohortsModel } from '~/models/cohortsModel'
import { groupsModel } from '~/models/groupsModel'
import { ActorGroupType, AnyCohortCriteriaType, AvailableFeature } from '~/types'

import type { Noun } from '../../../models/groupsModel'
import type { AnyPropertyFilter, GroupType, GroupTypeIndex, PropertyFilterValue } from '../../../types'

export interface CohortFieldLogicProps {
    cohortFilterLogicKey: string
    fieldKey: keyof AnyCohortCriteriaType
    criteria: AnyCohortCriteriaType
    onChange?: (newField: AnyCohortCriteriaType) => void
    /* Only used for selector fields */
    fieldOptionGroupTypes?: FieldOptionsType[]
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface cohortFieldLogicValues {
    aggregationLabel: (groupTypeIndex: number | null | undefined, deferToUserWording?: boolean) => Noun // groupsModel
    groupTypes: Map<GroupTypeIndex, GroupType> // groupsModel
    hasAvailableFeature: (feature: AvailableFeature, currentUsage?: number | undefined) => boolean // userLogic
    calculatedValue: (
        taxonomicGroupType: TaxonomicFilterGroupType
    ) => PropertyFilterValue | AnyPropertyFilter[] | undefined
    calculatedValueLoading: (taxonomicGroupType: TaxonomicFilterGroupType) => boolean
    currentOption: any
    fieldOptionGroups: FieldValues[]
    hasBehavioralCohortFiltering: boolean
    value: PropertyFilterValue | AnyPropertyFilter[] | undefined
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface cohortFieldLogicActions {
    onChange: (newField: AnyCohortCriteriaType) => {
        newField: AnyCohortCriteriaType
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface cohortFieldLogicMeta {
    key: string
    __keaTypeGenInternalSelectorTypes: {
        hasBehavioralCohortFiltering: (
            hasAvailableFeature: (feature: AvailableFeature, currentUsage?: number | undefined) => boolean
        ) => boolean
        fieldOptionGroups: (
            arg: any,
            groupTypes: Map<GroupTypeIndex, GroupType>,
            aggregationLabel: (groupTypeIndex: number | null | undefined, deferToUserWording?: boolean) => Noun,
            hasBehavioralCohortFiltering: boolean
        ) => FieldValues[]
        currentOption: (
            fieldOptionGroups: FieldValues[],
            value: PropertyFilterValue | AnyPropertyFilter[] | undefined
        ) => any
        calculatedValueLoading: (
            value: PropertyFilterValue | AnyPropertyFilter[] | undefined,
            criteria: AnyCohortCriteriaType,
            fieldKey: keyof import('~/types').CohortCriteriaType,
            allCohortsLoading: boolean,
            actionsLoading: boolean
        ) => (taxonomicGroupType: TaxonomicFilterGroupType) => boolean
        calculatedValue: (
            value: PropertyFilterValue | AnyPropertyFilter[] | undefined,
            criteria: AnyCohortCriteriaType,
            fieldKey: keyof import('~/types').CohortCriteriaType,
            allCohortsLoading: boolean,
            actionsLoading: boolean
        ) => (taxonomicGroupType: TaxonomicFilterGroupType) => PropertyFilterValue | AnyPropertyFilter[] | undefined
    }
}

export type cohortFieldLogicType = MakeLogicType<
    cohortFieldLogicValues,
    cohortFieldLogicActions,
    CohortFieldLogicProps,
    cohortFieldLogicMeta
>

export const cohortFieldLogic = kea<cohortFieldLogicType>([
    path(['scenes', 'cohorts', 'CohortFilters', 'cohortFieldLogic']),
    key((props) => `${props.cohortFilterLogicKey}`),
    props({} as CohortFieldLogicProps),
    connect(() => ({
        values: [groupsModel, ['groupTypes', 'aggregationLabel'], userLogic, ['hasAvailableFeature']],
    })),
    propsChanged(({ actions, props }, oldProps) => {
        if (props.fieldKey && !objectsEqual(props.criteria, oldProps.criteria)) {
            actions.onChange(props.criteria)
        }
    }),
    actions({
        onChange: (newField: AnyCohortCriteriaType) => ({ newField }),
    }),
    reducers(({ props }) => ({
        value: [
            resolveCohortFieldValue(props.criteria, props.fieldKey),
            {
                onChange: (_, { newField }) =>
                    resolveCohortFieldValue({ ...props.criteria, ...newField }, props.fieldKey),
            },
        ],
    })),
    selectors({
        hasBehavioralCohortFiltering: [
            (s) => [s.hasAvailableFeature],
            (hasAvailableFeature: (feature: AvailableFeature, currentUsage?: number | undefined) => boolean) =>
                hasAvailableFeature(AvailableFeature.BEHAVIORAL_COHORT_FILTERING),
        ],
        fieldOptionGroups: [
            (s) => [
                (_, props) => props.fieldOptionGroupTypes,
                s.groupTypes,
                s.aggregationLabel,
                s.hasBehavioralCohortFiltering,
            ],
            (
                fieldOptionGroupTypes,
                groupTypes: Map<import('~/types').GroupTypeIndex, import('~/types').GroupType>,
                aggregationLabel: (
                    groupTypeIndex: number | null | undefined,
                    deferToUserWording?: boolean
                ) => import('~/models/groupsModel').Noun,
                hasBehavioralCohortFiltering: boolean
            ): FieldValues[] => {
                const fieldOptions = hasBehavioralCohortFiltering
                    ? { ...FIELD_VALUES, ...SCALE_FIELD_VALUES }
                    : FIELD_VALUES

                const allGroups = {
                    ...fieldOptions,
                    [FieldOptionsType.Actors]: {
                        label: 'Actors',
                        type: FieldOptionsType.Actors,
                        values: {
                            [ActorGroupType.Person]: {
                                label: 'Persons',
                            },
                            ...Object.fromEntries(
                                Array.from(groupTypes.values())
                                    .map((type) => [
                                        `${ActorGroupType.GroupPrefix}_${type.group_type_index}`,
                                        { label: aggregationLabel(type.group_type_index).plural },
                                    ])
                                    .filter(Boolean)
                            ),
                        },
                    },
                }
                return [...(fieldOptionGroupTypes?.map((type: FieldOptionsType) => allGroups[type]) ?? [])]
            },
        ],
        currentOption: [
            (s) => [s.fieldOptionGroups, s.value],
            (
                fieldOptionGroups: FieldValues[],
                value: import('~/types').AnyPropertyFilter[] | import('~/types').PropertyFilterValue | undefined
            ) =>
                value && typeof value === 'string'
                    ? fieldOptionGroups.reduce(
                          (accumulator, group) => {
                              Object.assign(accumulator, group.values)
                              return accumulator
                          },
                          {} as Record<string, any>
                      )?.[value]
                    : null,
        ],
        calculatedValueLoading: [
            (s, p) => [
                s.value,
                p.criteria,
                p.fieldKey,
                cohortsModel.selectors.allCohortsLoading,
                actionsModel.selectors.actionsLoading,
            ],
            (
                value: import('~/types').AnyPropertyFilter[] | import('~/types').PropertyFilterValue | undefined,
                criteria: AnyCohortCriteriaType,
                fieldKey: keyof import('~/types').CohortCriteriaType,
                cohortsModelLoading: boolean,
                actionsModelLoading: boolean
            ) =>
                (taxonomicGroupType: TaxonomicFilterGroupType) => {
                    return (
                        (criteria.type === BehavioralFilterKey.Cohort &&
                            fieldKey === 'value_property' &&
                            typeof value === 'number' &&
                            cohortsModelLoading) ||
                        (taxonomicGroupType === TaxonomicFilterGroupType.Actions &&
                            typeof value === 'number' &&
                            actionsModelLoading)
                    )
                },
        ],
        calculatedValue: [
            (s, p) => [
                s.value,
                p.criteria,
                p.fieldKey,
                cohortsModel.selectors.allCohortsLoading,
                actionsModel.selectors.actionsLoading,
            ],
            (
                value: import('~/types').AnyPropertyFilter[] | import('~/types').PropertyFilterValue | undefined,
                criteria: AnyCohortCriteriaType,
                fieldKey: keyof import('~/types').CohortCriteriaType,
                cohortsModelLoading: boolean,
                actionsModelLoading: boolean
            ) =>
                (taxonomicGroupType: TaxonomicFilterGroupType) => {
                    // Only used by taxonomic filter field to determine label name
                    if (
                        criteria.type === BehavioralFilterKey.Cohort &&
                        fieldKey === 'value_property' &&
                        typeof value === 'number'
                    ) {
                        if (cohortsModelLoading) {
                            return 'Loading...'
                        }
                        return cohortsModel.findMounted()?.values?.cohortsById?.[value]?.name ?? `Cohort ${value}`
                    }
                    if (taxonomicGroupType === TaxonomicFilterGroupType.Actions && typeof value === 'number') {
                        if (actionsModelLoading) {
                            return 'Loading...'
                        }
                        return actionsModel.findMounted()?.values?.actionsById?.[value]?.name ?? `Action ${value}`
                    }
                    return value
                },
        ],
    }),
    listeners(({ props }) => ({
        onChange: ({ newField }) => {
            // Merge the partial field payload onto the existing criteria so
            // cleanBehavioralTypeCriteria sees `event_type` and `value` together — otherwise
            // a freshly-picked PersonMetadata property (whose `event_type` lives on the
            // criteria, not the payload) is derived as a plain Person type and matches nobody.
            props.onChange?.(cleanBehavioralTypeCriteria({ ...props.criteria, ...newField }))
        },
    })),
])
